import React from 'react';
import { caseStudies } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, Quote } from 'lucide-react';

const CaseStudies = () => {
  return (
    <section id="case-studies" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            Success Stories That Inspire
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Dive deep into how we've transformed businesses and delivered measurable results
          </p>
        </div>

        {/* Case Studies */}
        <div className="space-y-16">
          {caseStudies.map((study, index) => (
            <Card 
              key={study.id}
              className="overflow-hidden border-gray-200 shadow-lg hover:shadow-2xl transition-shadow duration-300"
            >
              <div className={`grid lg:grid-cols-2 gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Image */}
                <div className={`relative h-64 lg:h-auto ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img 
                    src={study.image} 
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
                </div>

                {/* Content */}
                <div className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div style={{ backgroundColor: '#D97904' }} className="inline-block text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                    {study.industry}
                  </div>
                  
                  <CardHeader className="p-0 mb-6">
                    <CardTitle 
                      className="text-3xl font-bold text-gray-900 mb-4"
                      style={{ fontFamily: 'var(--font-serif)' }}
                    >
                      {study.title}
                    </CardTitle>
                    <p className="text-lg font-semibold text-gray-700">{study.client}</p>
                  </CardHeader>

                  <CardContent className="p-0 space-y-6">
                    {/* Challenge */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 text-lg">The Challenge</h4>
                      <p className="text-gray-600 leading-relaxed">{study.challenge}</p>
                    </div>

                    {/* Solution */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 text-lg">Our Solution</h4>
                      <p className="text-gray-600 leading-relaxed">{study.solution}</p>
                    </div>

                    {/* Results */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-3 text-lg">Key Results</h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {study.results.map((result) => (
                          <div key={`${study.id}-${result.substring(0, 20)}`} className="flex items-start space-x-2">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Testimonial */}
                    <div className="bg-orange-50 p-6 rounded-lg border-l-4 relative" style={{ borderColor: '#D97904' }}>
                      <Quote className="absolute top-4 right-4 w-8 h-8 opacity-20" style={{ color: '#D97904' }} />
                      <p className="text-gray-700 italic mb-3 relative z-10">"{study.testimonial}"</p>
                      <p className="text-sm font-semibold" style={{ color: '#D97904' }}>— {study.author}</p>
                    </div>
                  </CardContent>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
